import CustomLoadingButton from 'Components/CustomLoadingButton'
import { WithLoader } from 'Components/WithLoader'
import { DOMICILE_CLASS_DRAWER_TAB_PARAM_KEY } from 'Services/ProductManagement/constants'
import type { ParameterGroup } from 'Services/ProductManagement/Features/Parameter/types'
import ProductPricingActivePeriodDrawer from 'Services/ProductManagement/Features/Product/Components/ProductPricing/ProductPricingActivePeriodDrawer'
import ProductPricingGrid from 'Services/ProductManagement/Features/Product/Components/ProductPricing/ProductPricingGrid'
import ProductPricingTabs from 'Services/ProductManagement/Features/Product/Components/ProductPricing/ProductPricingTabs'
import { useUpdateSingleProductMutation } from 'Services/ProductManagement/Features/Product/productApi'
import type {
  PricingPlan,
  Product,
  Validity
} from 'Services/ProductManagement/Features/Product/types'
import { PricingTabType } from 'Services/ProductManagement/Features/Product/types'
import type { ZoneDefinition } from 'Services/ProductManagement/Features/ZoneDefinition/types'
import { useGetSingleZoneDefinitionQuery } from 'Services/ProductManagement/Features/ZoneDefinition/zoneDefinitionApi'
import { useGridHighlight } from 'Util/Context/gridHighlightContext'
import { UnsavedChangesProvider, useUnsavedChanges } from 'Util/Context/unsavedChangesContext'
import getTranslation from 'Util/getTranslation'
import SnackbarUtils from 'Util/Snackbar'
import SaveIcon from '@mui/icons-material/Save'
import { TabPanel } from '@mui/lab'
import { Box, Button, ButtonGroup, Divider, styled, Typography, useTheme } from '@mui/material'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { t } from 'ttag'

interface StyledButtonGroupButtonProps {
  selected?: boolean
}

const StyledButtonGroupButton = styled(Button)<StyledButtonGroupButtonProps>(
  ({ theme, selected }) => ({
    borderRadius: 4,
    padding: theme.spacing(1, 1.5),
    fontWeight: selected ? 700 : 600,
    color: theme.palette.text[selected ? 'primary' : 'secondary'],
    backgroundColor: selected ? theme.palette.action.selected : 'transparent'
  })
)

interface ProductPricingProps {
  product: Product
  domicileClassParameterGroup?: ParameterGroup | null
  allowedTabs?: PricingTabType[]
}

function ProductPricing({
  product,
  domicileClassParameterGroup,
  allowedTabs
}: Readonly<ProductPricingProps>) {
  const theme = useTheme()
  const { hasUnsavedChanges, markEdited, clearEdited, confirmForUnsavedChanges } =
    useUnsavedChanges()
  const { editedCells, setEditedCells, setUpdatedCells, clearHighlightedCells } = useGridHighlight()
  const [searchParams, setSearchParams] = useSearchParams()

  const [updateSingleProduct, { isLoading: isSubmitLoading }] = useUpdateSingleProductMutation()
  const { data: zoneDefinition, isLoading: isZoneDefinitionLoading } =
    useGetSingleZoneDefinitionQuery(product.product_owner, {
      refetchOnMountOrArgChange: true
    })

  // Domicile class logic
  const domicileClasses = domicileClassParameterGroup?.parameters ?? []
  const hasDomicileClasses = domicileClasses.length > 0
  const domicileClassFromParams = searchParams.get(DOMICILE_CLASS_DRAWER_TAB_PARAM_KEY) ?? undefined
  const [selectedDomicileClass, setSelectedDomicileClass] = useState<string | undefined>(
    domicileClassFromParams ?? undefined
  )

  const [isActivePeriodDrawerOpen, setIsActivePeriodDrawerOpen] = useState(false)
  const [productPricingPlans, setProductPricingPlans] = useState<PricingPlan[]>(
    product.pricing_plans
  )
  const [activePeriods, setActivePeriods] = useState<Array<Validity | null>>([])
  const [selectedActivePeriod, setSelectedActivePeriod] = useState<Validity | null>(null)
  const [selectedPricingTab, setSelectedPricingTab] = useState<PricingTabType>(
    allowedTabs ? allowedTabs[0] : PricingTabType.PRICINGS
  )

  useEffect(() => {
    // If there are domicile classes but no tab is selected, default to the first domicile class
    if (hasDomicileClasses && !searchParams.get(DOMICILE_CLASS_DRAWER_TAB_PARAM_KEY)) {
      const defaultDomicileClass = domicileClasses[0].parameter_id
      setSelectedDomicileClass(defaultDomicileClass)
      searchParams.set(DOMICILE_CLASS_DRAWER_TAB_PARAM_KEY, defaultDomicileClass)
      setSearchParams(searchParams)
    }
  }, [domicileClasses])

  useEffect(() => {
    handleActivePeriodsChange()
  }, [productPricingPlans])

  useEffect(() => {
    setActivePeriods(generateDistinctActivePeriods())
  }, [])

  const handleDomicileClassChange = (newValue: string) => {
    confirmForUnsavedChanges(() => {
      setSelectedDomicileClass(newValue)
      searchParams.set(DOMICILE_CLASS_DRAWER_TAB_PARAM_KEY, newValue)
      setSearchParams(searchParams)
      setProductPricingPlans(product.pricing_plans)
      setActivePeriods(generateDistinctActivePeriods())
      clearHighlightedCells()
    })
  }

  const handleCloseActivePeriod = () => {
    setIsActivePeriodDrawerOpen(false)
    setSelectedActivePeriod(null)
  }

  const handleOpenActivePeriod = () => {
    setIsActivePeriodDrawerOpen(true)
  }

  const generateDistinctActivePeriods = (pricingPlans?: PricingPlan[]) => {
    // If product has no pricing plans, generate default active period automatically
    if ([...(pricingPlans || productPricingPlans)].length < 1) return [null]

    const uniquePeriods = [...(pricingPlans || productPricingPlans)].reduce(
      (uniquePeriods, plan) => {
        // Get the title for grouping
        const title = plan.active_period?.title ?? ''

        // Check if we already have a period with this title in our results
        const periodExists = uniquePeriods.some(activePeriod =>
          activePeriod === null ? title === '' : activePeriod.title === title
        )

        // If not, add it to our results
        if (!periodExists) {
          uniquePeriods.push(plan.active_period ? { ...plan.active_period } : null)
        }
        return uniquePeriods
      },
      [] as Array<Validity | null>
    )

    // Order uniquePeriods so that the one with null active period is always first
    return uniquePeriods.sort((a, b) => {
      if (a === null) return -1
      if (b === null) return 1
      return a.title.localeCompare(b.title)
    })
  }

  /**
   * Handles the change in active periods.
   * Merges the current active periods with new ones generated from pricing plans,
   * ensuring no duplicates are added.
   */
  const handleActivePeriodsChange = () => {
    // Start with the current active periods to preserve them
    const currentActivePeriods = [...activePeriods]

    // Add any new active periods from pricing plans
    const newActivePeriods = generateDistinctActivePeriods()

    // Merge the lists, avoiding duplicates
    const mergedPeriods = [...currentActivePeriods]

    for (const newPeriod of newActivePeriods) {
      const periodExists = mergedPeriods.some(activePeriod =>
        activePeriod === null
          ? newPeriod === null
          : newPeriod !== null && activePeriod.title === newPeriod.title
      )

      if (!periodExists) {
        mergedPeriods.push(newPeriod)
      }
    }

    if (mergedPeriods.length === 0) {
      mergedPeriods.push(null)
    }
    setActivePeriods(mergedPeriods)
  }

  const getPricingPlansByActivePeriod = (activePeriod: Validity | null, domicileClass?: string) => {
    let pricingPlansByActivePeriod: PricingPlan[] = []

    if (activePeriod === null) {
      pricingPlansByActivePeriod = productPricingPlans.filter(
        pricingPlan => pricingPlan.active_period === null
      )
    } else {
      pricingPlansByActivePeriod = productPricingPlans.filter(
        pricingPlan => pricingPlan.active_period?.title === activePeriod?.title
      )
    }

    // Filter by domicile class if provided
    if (domicileClass !== undefined) {
      return pricingPlansByActivePeriod.filter(
        pricingPlan => pricingPlan.domicile_class === domicileClass
      )
    }

    return pricingPlansByActivePeriod
  }

  /**
   * Validates that pricing plans for active periods of a given domicile class don't exceed zone limit.
   */
  const validateDomicileClassPricingPlanAmount = (
    domicileClassId: string,
    activePeriods: (Validity | null)[],
    totalZonesAmount: number
  ) => {
    return activePeriods.every(activePeriod => {
      const pricingPlans = getPricingPlansByActivePeriod(activePeriod, domicileClassId)
      return pricingPlans.length <= totalZonesAmount
    })
  }

  /**
   * Validates that pricing plans for active periods don't exceed zone limit.
   */
  const validateActivePeriodPricingPlanAmount = (
    activePeriods: (Validity | null)[],
    totalZonesAmount: number
  ) => {
    return activePeriods.every(activePeriod => {
      const pricingPlans = getPricingPlansByActivePeriod(activePeriod)
      return pricingPlans.length <= totalZonesAmount
    })
  }

  /**
   * Returns false if the number of pricing plans exceeds the amount of zones in the zone definition.
   *
   * One active period represents one pricing grid, which can have one pricing plan per zone.
   * Therefore the amount of pricing plans per active period cannot exceed the amount of zones in the zone definition.
   * This is checked for each domicile class if applicable.
   */
  const validatePricingPlanAmount = (
    zoneDefinition: ZoneDefinition,
    activePeriods: (Validity | null)[]
  ) => {
    const totalZonesAmount = zoneDefinition.zones.length + zoneDefinition.special_zones.length

    if (hasDomicileClasses) {
      return domicileClasses.every(domicileClass =>
        validateDomicileClassPricingPlanAmount(
          domicileClass.parameter_id,
          activePeriods,
          totalZonesAmount
        )
      )
    }

    return validateActivePeriodPricingPlanAmount(activePeriods, totalZonesAmount)
  }

  const handleSave = (updatedPricingPlans: PricingPlan[]) => {
    if (!zoneDefinition) {
      SnackbarUtils.error(
        t`Product pricing is not available when PTA does not have zone definition defined.`
      )
      return
    }

    if (!validatePricingPlanAmount(zoneDefinition, activePeriods)) {
      SnackbarUtils.error(t`Pricing plan amount cannot exceed the number of available zones.`)
      return
    }

    updateSingleProduct({
      id: product.id,
      pricing_plans: updatedPricingPlans
    })
      .unwrap()
      .then((productUpdateResponse: Product) => {
        setUpdatedCells(productUpdateResponse.pricing_plans.length > 0 ? editedCells : [])
        setEditedCells([])
        setProductPricingPlans([...productUpdateResponse.pricing_plans])
        clearEdited()
        SnackbarUtils.success(t`Successfully saved changes.`)
      })
      .catch(() => {
        SnackbarUtils.error(t`An error occurred while saving changes.`)
      })
  }

  const filterPlansByActivePeriod = (plans: PricingPlan[], period: Validity) => {
    return plans.filter(plan => plan.active_period?.title !== period.title)
  }

  const filterPeriodsByTitle = (periods: Array<Validity | null>, title: string) => {
    return periods.filter(period => period?.title !== title)
  }

  const handleDeleteActivePeriod = (activePeriod: Validity | null) => () => {
    if (activePeriod === null) {
      return
    } else {
      setActivePeriods(prevPeriods => filterPeriodsByTitle(prevPeriods, activePeriod.title))
      const updatedPricingPlans = filterPlansByActivePeriod(productPricingPlans, activePeriod)
      handleSave(updatedPricingPlans)
    }
  }

  const handleSelectActivePeriod = (activePeriod: Validity | null) => {
    setSelectedActivePeriod(activePeriod)
    setIsActivePeriodDrawerOpen(true)
  }

  const updateActivePeriod = (oldActivePeriod: Validity | null, newActivePeriod: Validity) => {
    setProductPricingPlans(prevPlans =>
      prevPlans.map(plan => {
        if (plan.active_period?.title === oldActivePeriod?.title) {
          return {
            ...plan,
            active_period: newActivePeriod
          }
        }
        return plan
      })
    )
    setActivePeriods(prevPeriods =>
      prevPeriods.map(period => {
        if (period?.title === oldActivePeriod?.title) {
          return newActivePeriod
        }
        return period
      })
    )
  }

  const handleActivePeriodSubmit = (activePeriod: Validity) => {
    if (selectedActivePeriod) {
      updateActivePeriod(selectedActivePeriod, activePeriod)
    } else {
      setActivePeriods(prevState => [...prevState, activePeriod])
    }
    // Mark the pricing as edited show that there are unsaved changes (in active periods)
    markEdited()
  }

  const renderPricingGrid = (pricingGridType: PricingTabType, activePeriod: Validity | null) => (
    <ProductPricingGrid
      pricingPlans={getPricingPlansByActivePeriod(activePeriod, selectedDomicileClass)}
      zoneDefinition={zoneDefinition as ZoneDefinition}
      product={product}
      handleOpenActivePeriod={handleOpenActivePeriod}
      activePeriod={activePeriod}
      setProductPricingPlans={setProductPricingPlans}
      pricingGridType={pricingGridType}
      {...(selectedDomicileClass && { domicileClass: selectedDomicileClass })}
    />
  )

  return (
    <>
      {hasDomicileClasses && (
        <>
          <Box
            sx={{
              backgroundColor: 'background.default',
              p: 2
            }}
          >
            <ButtonGroup
              variant="text"
              sx={{
                '.MuiButtonGroup-grouped:not(:last-of-type)': {
                  borderColor: theme.palette.action.selected
                }
              }}
              aria-label={t`Domicile class selection buttons`}
            >
              {domicileClasses.map(domicileClass => (
                <StyledButtonGroupButton
                  key={domicileClass.parameter_id}
                  size="small"
                  variant="text"
                  onClick={() => handleDomicileClassChange(domicileClass.parameter_id)}
                  selected={domicileClass.parameter_id === selectedDomicileClass}
                >
                  {getTranslation(domicileClass.translations, 'fi').value}
                </StyledButtonGroupButton>
              ))}
            </ButtonGroup>
          </Box>
          <Divider />
        </>
      )}
      <Box sx={{ p: 2 }}>
        <WithLoader loading={isZoneDefinitionLoading}>
          {zoneDefinition ? (
            <>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  py: 1
                }}
              >
                <Typography variant="subtitle2" fontWeight="bold">
                  {selectedPricingTab === PricingTabType.EXCHANGE_PERIOD
                    ? t`Exchange period by zone`
                    : t`Pricing by zone`}
                </Typography>
                <Typography
                  variant="subtitle2"
                  fontWeight="bold"
                  sx={{ color: theme.palette.text.secondary }}
                >
                  {getTranslation(product.translations, 'fi').name}
                </Typography>
              </Box>
              {activePeriods.map(activePeriod => {
                return (
                  <ProductPricingTabs
                    key={`pricing-tabs-${activePeriod?.title ?? 'default'}`}
                    activePeriod={activePeriod}
                    handleSelectActivePeriod={handleSelectActivePeriod}
                    handleDeleteActivePeriod={handleDeleteActivePeriod(activePeriod)}
                    onPricingTabChange={tabType => {
                      setSelectedPricingTab(tabType)
                      setProductPricingPlans(product.pricing_plans)
                      clearHighlightedCells()
                    }}
                    allowedTabs={allowedTabs}
                    selectedTab={selectedPricingTab}
                  >
                    {(!allowedTabs || allowedTabs.includes(PricingTabType.PRICINGS)) && (
                      <TabPanel sx={{ p: 0 }} value={PricingTabType.PRICINGS}>
                        {renderPricingGrid(PricingTabType.PRICINGS, activePeriod)}
                      </TabPanel>
                    )}
                    {(!allowedTabs || allowedTabs.includes(PricingTabType.EXCHANGE_PERIOD)) && (
                      <TabPanel sx={{ p: 0 }} value={PricingTabType.EXCHANGE_PERIOD}>
                        {renderPricingGrid(PricingTabType.EXCHANGE_PERIOD, activePeriod)}
                      </TabPanel>
                    )}
                  </ProductPricingTabs>
                )
              })}
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  mt: 2
                }}
              >
                <Button
                  onClick={() =>
                    confirmForUnsavedChanges(() => {
                      setProductPricingPlans(product.pricing_plans)
                      setActivePeriods(generateDistinctActivePeriods(product.pricing_plans))
                      clearEdited()
                      clearHighlightedCells()
                    })
                  }
                  disabled={!hasUnsavedChanges}
                  variant="outlined"
                  color="inherit"
                >
                  {t`Cancel`}
                </Button>
                <CustomLoadingButton
                  size="medium"
                  disabled={!hasUnsavedChanges}
                  onClick={() => handleSave(productPricingPlans)}
                  isLoading={isSubmitLoading}
                  icon={<SaveIcon />}
                  iconPosition="start"
                >
                  {t`Save changes`}
                </CustomLoadingButton>
              </Box>
            </>
          ) : (
            <Typography variant="subtitle2">
              {t`Product pricing is not available when the authority does not have zone definition defined.`}
            </Typography>
          )}
        </WithLoader>
        {/* An additional nested UnsavedChangesProvider for tracking the unsaved changes within the active period drawer */}
        <UnsavedChangesProvider>
          <ProductPricingActivePeriodDrawer
            open={isActivePeriodDrawerOpen}
            selectedActivePeriod={selectedActivePeriod}
            handleClose={handleCloseActivePeriod}
            activePeriods={activePeriods}
            onSubmit={handleActivePeriodSubmit}
            selectedTab={selectedPricingTab}
          />
        </UnsavedChangesProvider>
      </Box>
    </>
  )
}

export default ProductPricing
